import React, { useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { useTheme } from "styled-components";
import { ResponsiveLine } from "@nivo/line";
import { formatInTimeZone } from "date-fns-tz";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { dateFnsLocaleFor } from "../../../../lib/dateUtils";
import Selector from "../../../../components/selector";
import { dateFilter } from "../../../shared/crud/filter-widgets";
import { useHotelTimeZone } from "../application/hooks/useHotelTimeZone";
import { resolveMetricRange, InvalidHotelTimeZoneError } from "../domain/metricRangeResolver";
import { useExperimentResults } from "../application/hooks/useExperimentResults";
import {
  DateRange,
  ExperimentEntity,
  ExperimentStatus,
  Granularity,
  MetricRange,
  PausePeriodEntity,
  TimeSeriesPointEntity,
} from "../domain/experiment.entity";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { messages } from "../experiment.messages";

export interface ExperimentEvolutionChartProps {
  propertyTicker: string;
  experiment: ExperimentEntity;
  repository: ExperimentRepository;
  // Resolved (Decision 1): range/custom (and their setters) are lifted into ExperimentResultsPage
  // (US-017) — this is the one HU3 section that receives both the values and their setters, since
  // it owns the actual range dropdown UI that changes them.
  range: MetricRange;
  custom: DateRange | undefined;
  onRangeChange: (range: MetricRange) => void;
  onCustomChange: (custom: DateRange | undefined) => void;
}

export type ChartMetricKey =
  | "conversion_rate"
  | "revenue_per_visit"
  | "total_revenue"
  | "total_bookings"
  | "average_booking_value";

export type ChartMetricUnit = "percent" | "currency" | "count";

// `desc` is a nested sub-object (per-metric tooltip copy for AllMetricsTable), not itself a
// MessageDescriptor — excluded here so `messages.metrics[labelKey]` stays a valid formatMessage arg.
type MetricLabelKey = Exclude<keyof typeof messages.metrics, "desc">;

export const CHART_METRICS: ReadonlyArray<{
  key: ChartMetricKey;
  unit: ChartMetricUnit;
  labelKey: MetricLabelKey;
}> = [
  { key: "revenue_per_visit", unit: "currency", labelKey: "revenuePerVisit" },
  { key: "total_revenue", unit: "currency", labelKey: "revenueTotal" },
  { key: "total_bookings", unit: "count", labelKey: "bookings" },
  { key: "conversion_rate", unit: "percent", labelKey: "conversionRate" },
  { key: "average_booking_value", unit: "currency", labelKey: "avgBookingValue" },
];

const RANGE_OPTIONS: ReadonlyArray<{
  value: MetricRange;
  labelKey: keyof typeof messages.resultsChart.range;
}> = [
  { value: "LAST_7", labelKey: "last7" },
  { value: "LAST_15", labelKey: "last15" },
  { value: "LAST_30", labelKey: "last30" },
  { value: "FULL", labelKey: "full" },
  { value: "CUSTOM", labelKey: "custom" },
];

/** Stable ids so each dropdown's caption can be a real <label htmlFor>. Only one evolution chart
 *  is ever mounted per results page, so a fixed id is unambiguous. */
const METRIC_SELECT_ID = "experiment-evolution-metric";
const RANGE_SELECT_ID = "experiment-evolution-range";

interface DateRangeWidgetValue {
  startDate: string | null;
  endDate: string | null;
}

function toUtcMidnight(isoDateOnly: string): Date {
  const [y, m, d] = isoDateOnly.split("-").map(Number); // "YYYY-MM-DD"
  return new Date(Date.UTC(y, m - 1, d));
}

// Business Rule #7: x-axis tick labels are derived from `labelKey`, falling back to a formatted
// `bucketStart` date for any pattern not covered by `results.week` ("Sem {n}") — e.g. the "day_N"
// daily-bucket pattern, which has no dedicated message.
export function labelFor(
  point: TimeSeriesPointEntity,
  intl: IntlShape,
  timeZone: string | undefined
): string {
  const weekMatch = /^week_(\d+)$/.exec(point.labelKey);
  if (weekMatch) {
    return intl.formatMessage(messages.resultsChart.week, { n: weekMatch[1] });
  }
  // The bucket is a hotel-local day, so it is named in the HOTEL's zone and in the UI's own
  // language — never in whatever zone and locale the browser happens to run in.
  return formatInTimeZone(point.bucketStart, timeZone || "UTC", "d MMM", {
    locale: dateFnsLocaleFor(intl.locale),
  });
}

// Business Rule #10: the Y-axis/tooltip format function is derived from CHART_METRICS' `unit`
// field for the currently selected metric — never hardcoded to `%`. Exported standalone so its
// unit-mapping logic is directly unit-testable without needing `@nivo/line`'s SVG output to
// actually paint under jsdom (AC5).
export function formatMetricValue(
  unit: ChartMetricUnit,
  value: number,
  currency: string,
  intl: IntlShape
): string {
  if (unit === "percent") {
    return `${intl.formatNumber(value, { maximumFractionDigits: 2 })}%`;
  }
  if (unit === "currency") {
    // Business Rule 7: a `partial` response sends `currency: ""`, and `style: "currency"` throws
    // on an empty code. The plain number is drawn instead, as the scenario cards already do.
    if (!currency) {
      return intl.formatNumber(value, { maximumFractionDigits: 0 });
    }
    return intl.formatNumber(value, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
  }
  return intl.formatNumber(value, { maximumFractionDigits: 0 });
}

/**
 * The bucket size the backend actually used, read off the points themselves rather than re-derived
 * from the requested range. A series that mixes granularities (which the per-point field permits)
 * deliberately yields `undefined` — no caption is better than an arbitrary one.
 */
export function seriesGranularity(series: TimeSeriesPointEntity[]): Granularity | undefined {
  if (series.length === 0) return undefined;
  const first = series[0].granularity;
  return series.every(point => point.granularity === first) ? first : undefined;
}

export interface BandPoint {
  x: string;
  low: number;
  high: number;
}

/**
 * The confidence band for one scenario, as `{x, low, high}` triples. Returns an empty array for
 * every metric other than `conversion_rate`, and for any point whose bounds the backend left unset
 * — which is exactly how the chart knows there is no band to draw.
 */
export function buildBand(
  series: TimeSeriesPointEntity[],
  metric: ChartMetricKey,
  side: "a" | "b",
  intl: IntlShape,
  timeZone: string | undefined
): BandPoint[] {
  if (metric !== "conversion_rate") return [];
  const lowKey = side === "a" ? "ciLowA" : "ciLowB";
  const highKey = side === "a" ? "ciHighA" : "ciHighB";
  const band: BandPoint[] = [];
  series.forEach(point => {
    const value = point.metrics[metric];
    const low = value?.[lowKey];
    const high = value?.[highKey];
    if (low == null || high == null) return;
    band.push({ x: labelFor(point, intl, timeZone), low, high });
  });
  return band;
}

/**
 * The y-axis window for the lines and their confidence bands. nivo's default anchors the axis at
 * 0, which flattens a 2.9 % vs 3.5 % comparison into two parallel lines at the top of an empty
 * chart. The window is fitted to the data instead: the lowest and highest value across both
 * series and both bands, padded by 15 % of that range (at least one tick of 0.1) so the extremes
 * never touch the edges, and never below 0 for a metric that cannot be negative. Returns
 * `undefined` when there is nothing to fit, so nivo falls back to its own defaults.
 */
export function yDomainFor(
  series: Array<{ y: number | null }>,
  bands: BandPoint[]
): { min: number; max: number } | undefined {
  const values: number[] = [];
  series.forEach(p => {
    if (p.y !== null && Number.isFinite(p.y)) values.push(p.y);
  });
  bands.forEach(b => {
    if (Number.isFinite(b.low)) values.push(b.low);
    if (Number.isFinite(b.high)) values.push(b.high);
  });
  if (values.length === 0) return undefined;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = Math.max((hi - lo) * 0.15, 0.1);
  return { min: Math.max(0, lo - pad), max: hi + pad };
}

/**
 * A nivo custom layer painting one scenario's confidence band.
 *
 * Rendered as a layer rather than as four extra `ResponsiveLine` series on purpose (design.md,
 * Decision 5): extra series would put four meaningless entries in the legend, four more rows in
 * every slice tooltip, and would let the user hover a bound as if it were a value. Inserted before
 * `"lines"` in `layers`, so the lines stay on top of their own band.
 */
function ciBandLayer(band: BandPoint[], color: string, testId: string) {
  return function CiBand({ xScale, yScale }: any) {
    if (band.length === 0) return null;
    const x = (value: string) => Number(xScale(value));
    const y = (value: number) => Number(yScale(value));
    // One closed polygon: along the upper bound left-to-right, back along the lower bound.
    const top = band.map(point => `${x(point.x)},${y(point.high)}`);
    const bottom = [...band].reverse().map(point => `${x(point.x)},${y(point.low)}`);
    const d = `M ${[...top, ...bottom].join(" L ")} Z`;
    return <path d={d} fill={color} fillOpacity={0.15} stroke="none" data-testid={testId} />;
  };
}

/**
 * The x-axis labels (and only those) at which a pause marker should be drawn. `xScale` is
 * `type: "point"`, so a marker's value has to be one of the rendered labels: each pause is snapped
 * to the bucket that contains its start, falling back to the last bucket that starts before it when
 * the backend sent no `bucketEnd`. Pauses outside the plotted range draw nothing, and two pauses in
 * one bucket collapse to a single marker.
 */
export function pauseMarkerLabels(
  pauses: PausePeriodEntity[],
  series: TimeSeriesPointEntity[],
  intl: IntlShape,
  timeZone: string | undefined
): string[] {
  const labels: string[] = [];
  pauses.forEach(pause => {
    const at = pause.from.getTime();
    const index = series.findIndex((point, i) => {
      const start = point.bucketStart.getTime();
      if (at < start) return false;
      // The bucket's window is its own `bucketEnd` when the backend sent one, otherwise the next
      // bucket's start. The last bucket of a series with no `bucketEnd` is therefore unbounded, so
      // a pause after the plotted range would snap to it — acceptable, since a real warehouse
      // series always carries `bucketEnd`.
      const end = point.bucketEnd?.getTime() ?? series[i + 1]?.bucketStart.getTime();
      return end === undefined || at < end;
    });
    if (index === -1) return;
    const label = labelFor(series[index], intl, timeZone);
    // Two pauses inside one bucket draw one marker — nivo would otherwise stack two identical
    // vertical lines and two overlapping legends at the same x.
    if (!labels.includes(label)) labels.push(label);
  });
  return labels;
}

export const ExperimentEvolutionChart: React.FC<ExperimentEvolutionChartProps> = ({
  propertyTicker,
  experiment,
  repository,
  range,
  custom,
  onRangeChange,
  onCustomChange,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const hotelTimeZone = useHotelTimeZone();
  const [metric, setMetric] = useState<ChartMetricKey>("conversion_rate");
  const [focused, setFocused] = useState<string | undefined>();

  // Business Rule #1 (resolved, Decision 6): DRAFT/UNSPECIFIED short-circuits before any network
  // call, using useExperimentResults' real `enabled` option. Business Rule #4's
  // incomplete-custom-pick case is guarded inside that hook, for every section at once.
  const isDraftLike =
    experiment.status === ExperimentStatus.EXPERIMENT_STATUS_DRAFT ||
    experiment.status === ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED;
  const isEnabled = !isDraftLike;

  const { data, isLoading, error } = useExperimentResults(
    propertyTicker,
    experiment.id,
    range,
    repository,
    // Passed unconditionally, exactly like every sibling section: the query key itself is what
    // normalises a stale `custom` away for a non-CUSTOM range, so all of them de-duplicate onto
    // one request instead of the chart opening a second one.
    custom,
    { enabled: isEnabled }
  );

  // Business Rule #6: resolved purely for display (the "showing data from X to Y" caption) —
  // never passed into useExperimentResults. Skipped while hotelTimeZone is still loading or the
  // custom range is incomplete; degrades to `undefined` (no caption) on InvalidHotelTimeZoneError.
  const resolvedRange = useMemo(() => {
    if (!hotelTimeZone) return undefined;
    if (range === "CUSTOM" && !custom) return undefined;
    try {
      return resolveMetricRange(
        range,
        hotelTimeZone,
        {
          startedAt: experiment.startedAt,
          archivedAt: null, // always null today — see proposal's Plan vs. Actual point 3
        },
        custom
      );
    } catch (e) {
      if (e instanceof InvalidHotelTimeZoneError) return undefined;
      throw e;
    }
  }, [range, custom, hotelTimeZone, experiment.startedAt]);

  const dateFnsLocale = dateFnsLocaleFor(intl.locale);

  const activeMetric = CHART_METRICS.find(m => m.key === metric)!;

  const points = data?.series ?? [];

  // Null points are KEPT (they used to be filtered out): nivo breaks a line on a null `y`, so a day
  // with no data now reads as the gap it is instead of a straight segment bridging it. `enableArea`
  // stays off for the same reason.
  const seriesA = points.map(p => ({
    x: labelFor(p, intl, hotelTimeZone),
    y: p.metrics[metric]?.a ?? null,
  }));
  const seriesB = points.map(p => ({
    x: labelFor(p, intl, hotelTimeZone),
    y: p.metrics[metric]?.b ?? null,
  }));
  const hasAnyData = seriesA.some(p => p.y !== null) || seriesB.some(p => p.y !== null);

  // The band is only ever drawn for conversion_rate: it is the one metric with a proportion to
  // bracket, and the backend only populates the bounds there.
  const bandA = buildBand(points, metric, "a", intl, hotelTimeZone);
  const bandB = buildBand(points, metric, "b", intl, hotelTimeZone);

  const granularity = seriesGranularity(points);
  const pauseLabels = pauseMarkerLabels(data?.pauses ?? [], points, intl, hotelTimeZone);
  const yDomain = yDomainFor([...seriesA, ...seriesB], [...bandA, ...bandB]);

  function formatAxisValue(value: number): string {
    return formatMetricValue(activeMetric.unit, value, data?.scenarioA.currency ?? "", intl);
  }

  const metricOptions = CHART_METRICS.map(m => ({
    value: m.key,
    label: intl.formatMessage(messages.metrics[m.labelKey]),
  }));
  const selectedMetricOption = metricOptions.find(o => o.value === metric) ?? null;

  const rangeOptions = RANGE_OPTIONS.map(r => ({
    value: r.value,
    label: intl.formatMessage(messages.resultsChart.range[r.labelKey]),
  }));
  const selectedRangeOption = rangeOptions.find(o => o.value === range) ?? null;

  // The DateRangePicker's own displayed value is tracked as separate, local draft state (rather
  // than derived fresh from `custom` on every render) so that a half-completed pick (only one of
  // startDate/endDate typed so far) survives being echoed back as a controlled `value` prop —
  // `custom` itself only ever transitions to a defined value once BOTH ends are picked (Business
  // Rule #4), so deriving the widget's `value` directly from `custom` would otherwise wipe
  // whichever end was just typed as soon as the other end is still missing.
  const [customDraft, setCustomDraft] = useState<DateRangeWidgetValue>(() => ({
    startDate: custom?.from ? custom.from.toISOString().slice(0, 10) : null,
    endDate: custom?.to ? custom.to.toISOString().slice(0, 10) : null,
  }));

  // `calendarDay` makes the widget emit the picked day as `YYYY-MM-DD`. Without it the widget
  // normalises through `moment.utc().startOf("day")` over a local-midnight moment, which east of
  // UTC yields the previous day — a Madrid user picking 16-20/01 would query 15-19/01.
  const DateRangeWidget = useMemo(
    () => dateFilter("experimentEvolutionChartCustomRange", intl, { calendarDay: true }),
    [intl]
  );

  // Business Rule #5: the widget's {startDate, endDate} output is converted to UTC-midnight Date
  // objects at the boundary, never new Date(isoString)/new Date(y, m, d).
  const handleCustomChange = (dates: DateRangeWidgetValue) => {
    setCustomDraft(dates);
    if (dates.startDate && dates.endDate) {
      onCustomChange({
        from: toUtcMidnight(dates.startDate),
        to: toUtcMidnight(dates.endDate),
      });
    } else {
      onCustomChange(undefined);
    }
  };

  const selectors = (
    <div className="d-flex flex-wrap gap-3 mb-3">
      <div style={{ minWidth: 220 }}>
        {/* A real <label> bound to react-select's own input: the caption above each dropdown used
            to be a plain div, which left both of them unnamed for a screen reader. */}
        <label htmlFor={METRIC_SELECT_ID} className="text-muted small mb-1 d-block">
          <FormattedMessage {...messages.resultsChart.metricSelector} />
        </label>
        <Selector
          inputId={METRIC_SELECT_ID}
          isClearable={false}
          options={metricOptions}
          value={selectedMetricOption}
          onChange={(opt: { value: ChartMetricKey } | null) => opt && setMetric(opt.value)}
        />
      </div>
      <div style={{ minWidth: 200 }}>
        <label htmlFor={RANGE_SELECT_ID} className="text-muted small mb-1 d-block">
          <FormattedMessage {...messages.resultsChart.rangeSelector} />
        </label>
        <Selector
          inputId={RANGE_SELECT_ID}
          isClearable={false}
          options={rangeOptions}
          value={selectedRangeOption}
          onChange={(opt: { value: MetricRange } | null) => opt && onRangeChange(opt.value)}
        />
      </div>
      {range === "CUSTOM" && (
        <div>
          <div className="text-muted small mb-1">&nbsp;</div>
          <DateRangeWidget
            value={customDraft}
            onChange={handleCustomChange}
            placeholder={intl.formatMessage(messages.resultsChart.rangeSelector)}
            onFocusChange={setFocused}
            focused={focused}
          />
        </div>
      )}
    </div>
  );

  const granularityLabel =
    granularity === Granularity.GRANULARITY_DAY
      ? intl.formatMessage(messages.resultsChart.granularityDay)
      : granularity === Granularity.GRANULARITY_WEEK
      ? intl.formatMessage(messages.resultsChart.granularityWeek)
      : null;

  const caption =
    resolvedRange || granularityLabel ? (
      <div className="text-muted small mb-3" data-testid="chart-caption">
        {resolvedRange && (
          <>
            {formatInTimeZone(new Date(resolvedRange.from), hotelTimeZone || "UTC", "d MMM yyyy", {
              locale: dateFnsLocale,
            })}{" "}
            –{" "}
            {formatInTimeZone(new Date(resolvedRange.to), hotelTimeZone || "UTC", "d MMM yyyy", {
              locale: dateFnsLocale,
            })}
          </>
        )}
        {/* Stated explicitly from the data's own `granularity`, rather than inferred from the
            requested range. */}
        {resolvedRange && granularityLabel && " · "}
        {granularityLabel && <span data-testid="granularity-label">{granularityLabel}</span>}
      </div>
    ) : null;

  const title = (
    <h6 className="mb-3">
      <FormattedMessage
        {...messages.resultsChart.title}
        values={{ metric: intl.formatMessage(messages.metrics[activeMetric.labelKey]) }}
      />
    </h6>
  );

  // Rendered states, in priority order (Component section):
  // 1. isDraftLike -> draft placeholder (AC6)
  if (isDraftLike) {
    return (
      <Card className="mb-4">
        <Card.Body>
          {title}
          <div className="bg-light p-5 text-center text-muted rounded">
            <p className="mb-0">
              <FormattedMessage {...messages.resultsChart.draftPlaceholder} />
            </p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-2">
      <Card.Body>
        {title}
        {selectors}
        {caption}
        {isLoading ? (
          <div className="text-center text-muted p-5">
            <FormattedMessage {...messages.results.loading} />
          </div>
        ) : error ? (
          <div className="bg-light p-5 text-center text-muted rounded">
            <p className="mb-0">
              <FormattedMessage {...messages.resultsChart.dataUnavailable} />
            </p>
          </div>
        ) : !hasAnyData ? (
          // Previously a dead i18n key: an empty (or all-null) series painted an empty plot with
          // only a legend, which reads as a broken chart rather than as "nothing to show yet".
          <div className="bg-light p-5 text-center text-muted rounded" data-testid="chart-no-data">
            <p className="mb-0">
              <FormattedMessage {...messages.resultsChart.noData} />
            </p>
          </div>
        ) : (
          <div style={{ height: "350px" }}>
            <ResponsiveLine
              data={[
                { id: experiment.variants[0].ticker, data: seriesA },
                { id: experiment.variants[1].ticker, data: seriesB },
              ]}
              colors={[theme.orangeBackground, theme.darkBlueBackground]}
              margin={{ top: 20, right: 30, bottom: 70, left: 60 }}
              xScale={{ type: "point" }}
              yScale={{
                type: "linear",
                stacked: false,
                reverse: false,
                // Fitted to the data (see yDomainFor): a 0-anchored axis hides the difference
                // between two conversion rates that live within a percentage point of each other.
                min: yDomain?.min ?? "auto",
                max: yDomain?.max ?? "auto",
              }}
              curve="monotoneX"
              // Bands go under the lines; everything else keeps nivo's default order.
              layers={[
                "grid",
                "markers",
                "axes",
                "areas",
                ciBandLayer(bandA, theme.orangeBackground, "ci-band-a"),
                ciBandLayer(bandB, theme.darkBlueBackground, "ci-band-b"),
                "crosshair",
                "lines",
                "points",
                "slices",
                "mesh",
                "legends",
              ]}
              markers={pauseLabels.map(label => ({
                axis: "x" as const,
                value: label,
                lineStyle: { stroke: "#999", strokeWidth: 1, strokeDasharray: "4 4" },
                legend: intl.formatMessage(messages.resultsChart.pauseMarker),
                legendOrientation: "vertical" as const,
                textStyle: { fontSize: 10, fill: "#999" },
              }))}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -45,
                legend: "",
                legendOffset: 36,
                legendPosition: "middle",
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "",
                legendOffset: -40,
                legendPosition: "middle",
                format: formatAxisValue,
              }}
              enableGridX={false}
              enableGridY={true}
              pointSize={6}
              pointColor={{ theme: "background" }}
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              pointLabelYOffset={-12}
              useMesh={true}
              enableSlices="x"
              sliceTooltip={({ slice }) => {
                return (
                  <div
                    style={{
                      background: "white",
                      padding: "9px 12px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}>
                    <div style={{ marginBottom: "4px", fontWeight: "bold" }}>
                      {slice.points[0].data.xFormatted}
                    </div>
                    {slice.points.map(point => (
                      <div
                        key={point.id}
                        style={{
                          color: point.serieColor,
                          padding: "3px 0",
                          display: "flex",
                          alignItems: "center",
                        }}>
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            backgroundColor: point.serieColor,
                            marginRight: "8px",
                          }}
                        />
                        <span style={{ marginRight: "8px" }}>{point.serieId}:</span>
                        <strong>{formatAxisValue(Number(point.data.y))}</strong>
                      </div>
                    ))}
                  </div>
                );
              }}
              legends={[
                {
                  anchor: "bottom",
                  direction: "row",
                  justify: false,
                  translateX: 0,
                  translateY: 70,
                  itemsSpacing: 20,
                  itemDirection: "left-to-right",
                  itemWidth: 180,
                  itemHeight: 20,
                  itemOpacity: 0.75,
                  symbolSize: 12,
                  symbolShape: "circle",
                  symbolBorderColor: "rgba(0, 0, 0, .5)",
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemBackground: "rgba(0, 0, 0, .03)",
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
